import React, { useState, useEffect, Fragment } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Image,
  User,
} from '@nextui-org/react';

import {
  useInfiniteQuery,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { GoComment } from 'react-icons/go';
import { FaRegEdit } from 'react-icons/fa';
import { MdRocketLaunch } from 'react-icons/md';
import PostDropdown from '../PostDropdown/PostDropdown';
import CommentReplyBox from '../CommentReplyBox/CommentReplyBox';
import moment from 'moment';
import { decryptData } from '../../utils/encryptData';
import { formatName } from '../../utils/nameFormatter';
import { useSelector } from 'react-redux';
import Loading from '../Loading/Loading';
import { message } from 'antd';
import {
  fetchAllPosts,
  deletePost,
  addComment,
  addLike,
} from '../../services/querys ';
import CommentSection from '../CommentSection/CommentSection';
import { postValidate } from '../../utils/Validation/postConstraint';
import EditPost from '../../page/Community/EditPost';

function Post({ id, status }) {
  const [replyBox, setReplyBox] = useState(false);
  const [pId, setPid] = useState();
  const [likeStates, setLikeStates] = useState({});

  const { uid, rl } = useSelector((state) => state.auth);
  const loggedInUserId = decryptData(uid);
  const role = decryptData(rl);

  const { ref, inView } = useInView();

  const queryClient = useQueryClient();

  //get all post using infinite scrolling
  const { data, error, isFetchingNextPage, fetchNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['posts', id],
      queryFn: async ({ pageParam }) => fetchAllPosts({ pageParam, id }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.length ? allPages.length + 1 : undefined;
      },
      // refetchOnWindowFocus: false
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  // Delete post function
  const handleDeletePost = (postId) => {
    if (!postId) {
      message.error('Post Id not found!');
      return;
    }
  };

  if (error) {
    console.error('Fetch All Post: ', error);
    message.error(error.message);
  }

  //handle add comments
  const { mutate: addCommentMutation, isLoading: isAddingComment } =
    useMutation({
      mutationFn: addComment,
      onError: (error) => {
        console.error('Error adding comment:', error);
        message.error(error?.response?.data?.error || error.message);
      },
      onSuccess: async () => {
        message.success('Comment added successfully');
        await queryClient.invalidateQueries(['posts']);
      },
    });

  //add comment
  const handelAddComment = async (postId, comment, image) => {
    if (role !== 'admin' && role !== 'user' && role !== 'editor') {
      message.error('Please create an account.');
      return;
    }

    if (!postId) {
      message.error('Post id not found!');
      return;
    }
    //validation
    const errors = await postValidate({ description: comment });

    if (Object.keys(errors).length > 0) {
      message.error(errors.description);
      return;
    }
    addCommentMutation({ postId, comment, image });
  };

  //add like
  const { mutate: addLikeMutation } = useMutation({
    mutationFn: addLike,
    onError: (error) => {
      console.error('Error adding Like:', error);
      message.error(error?.response?.data?.error || error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['posts']);
    },
  });

  //add like
  const handelAddLike = (postId) => {
    if (role !== 'admin' && role !== 'user' && role !== 'editor') {
      message.error('Please create an account.');
      return;
    }

    if (!postId) {
      message.error('Post id not found!');
      return;
    }
    const newLikeState = !likeStates[postId];
    setLikeStates((prevState) => ({
      ...prevState,
      [postId]: newLikeState,
    }));

    addLikeMutation({ postId, like: newLikeState });
  };

  //delete post
  const { mutate: deletePostMutation, isLoading: isDeleteLoading } =
    useMutation({
      mutationFn: deletePost,
      onError: (error) => {
        console.error('Error Delete Post:', error);
        message.error(error?.response?.data?.error || error.message);
      },
      onSuccess: async () => {
        message.success('Post Delete successfully');
        await queryClient.invalidateQueries(['posts']);
      },
    });

  //delete post
  const handelDeletePost = async (postId) => {
    if (!postId) {
      message.error('Post id not found!');
      return;
    }
    deletePostMutation(postId);
  };

  //open reply box
  const toggleReplyBox = (isOpen, postId) => {
    setPid(postId);
    setReplyBox(isOpen);
  };

  return (
    <>
      <Loading isLoading={isLoading} />
      {data?.pages.map((page, pageIndex) => (
        <Fragment key={pageIndex}>
          {page?.map((post, index) => (
            <Card
              key={index}
              className="bg-background-secondary-color mt-5 font-poppins"
            >
              <CardHeader>
                <div className="flex justify-between items-center">
                  <User
                    name={
                      post?.userName
                        ? formatName(post?.userName).shortname
                        : 'Unknown User'
                    }
                    description={moment(post?.createdAt).fromNow()}
                    avatarProps={{
                      name: post?.userName
                        ? formatName(post?.userName).lastName
                        : 'Unknown',
                    }}
                    showFallback
                    src="https://images.unsplash.com/broken"
                    className="text-gray-100 capitalize"
                  />
                </div>

                {(post?.userId === loggedInUserId || role === 'admin') && (
                  <EditPost post={post} />
                )}
              </CardHeader>
              <CardBody className="-mt-1">
                <p className="text-start text-gray-300 whitespace-pre-line text-xs">
                  {post?.description}
                </p>
                {post?.image?.length > 0 && post?.image[0]?.url && (
                  <Image
                    loading="lazy"
                    alt="community post"
                    src={post.image[0].url}
                    className="w-full mt-4"
                  />
                )}
              </CardBody>
              <CardFooter className="flex justify-between gap-6">
                <div className="flex justify-between md:justify-start gap-6">
                  {/* Likes */}
                  <Button
                    onPress={() => handelAddLike(post?.postId)}
                    startContent={
                      post?.reactions?.some(
                        (reaction) => reaction?.userId === loggedInUserId
                      ) ? (
                        <MdRocketLaunch className="text-btn-color" size={20} />
                      ) : (
                        <MdRocketLaunch size={20} />
                      )
                    }
                    className="bg-stone-700 text-gray-200 justify-start text-xs"
                    size="sm"
                  >
                    {post.likes || 0}
                  </Button>

                  {/* Comments */}
                  <Button
                    onPress={() => toggleReplyBox(!replyBox, post?.postId)}
                    startContent={
                      <GoComment size={20} className="border-red-500" />
                    }
                    className="bg-stone-700 text-gray-200 justify-start text-xs "
                    size="sm"
                  >
                    Reply
                  </Button>
                </div>
                <p className="text-start text-gray-300 whitespace-pre-line">
                  {post?.reactions?.userId}
                </p>
                {/* Options for post owner */}
                {(post?.userId === loggedInUserId || role === 'admin') && (
                  <PostDropdown
                    deleteItem={() => handelDeletePost(post.postId)}
                  />
                )}
              </CardFooter>

              {/* Reply Box */}
              {replyBox && pId === post?.postId && (
                <CommentReplyBox
                  isLoading={isAddingComment}
                  onPress={() => toggleReplyBox(false, null)}
                  onSubmit={(comment, image) =>
                    handelAddComment(post?.postId, comment, image)
                  }
                />
              )}

              {/* Comments Section */}
              <CommentSection postId={post?.postId} />
            </Card>
          ))}
        </Fragment>
      ))}

      {/* Loading */}
      <div ref={ref} className="my-8">
        {isFetchingNextPage ? <Loading isLoading={isFetchingNextPage} /> : null}
      </div>
    </>
  );
}

export default Post;
